import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref } from 'vue';

import type { ProfileType } from '@/@types/graphql-types';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import type { ProfileInfoFragment } from '@/features/user-profile/graphql/fragments/ProfileInfo.fragment';
import type { TextRecommendationFragment } from '@/features/why-to-watch/graphql/fragments/TextRecommendation.fragment';

import TextRecommendationCardTitleDetailProfile from '@/features/why-to-watch/components/TextRecommendationCardTitleDetailProfile.vue';
import { TextRecommendationModalV2, TextRecommendationCardTitleDetailModal } from '@/lazy-load-components';
import TrailerModal from '@/components/TrailerModal.vue';
import Popover from '@/ui-components/Popover.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import { useTextRecommendation } from '@/helpers/composables/useTextRecommendation';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import type { SnowplowContext } from '@/helpers/tracking/providers';
import { useRoute } from 'vue-router/composables';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { toastSuccess, toastError } from '@/helpers/vfm/toast-helper';
import { useMutation } from '@/helpers/composables/useApollo';

import {
	DeleteTextRecommendationDocument,
	DeleteTextRecommendationMutation,
	DeleteTextRecommendationMutationVariables,
} from '@/features/why-to-watch/graphql/mutations/DeleteTextRecommendation.mutation';

import { faCheck, faPlay, faEllipsis, faPencil, faTrash } from '@fortawesome/free-solid-svg-icons';

import { disableScroll, enableScroll } from '@/helpers/scroll-helper';

export default /*#__PURE__*/_defineComponent({
  __name: 'TextRecommendationCardTitleDetail',
  props: {
    textRecommendation: null,
    title: null,
    isSingleTextRecommendation: { type: Boolean, default: false },
    hideMedia: { type: Boolean, default: false },
    profile: null,
    hideBadge: { default: () => [] },
    hideAuthor: { type: Boolean },
    additionalContexts: { default: () => [] }
  },
  emits: ["posterClick"],
  setup(__props: any) {

const props = __props as { textRecommendation: TextRecommendationFragment, title?: TitleDetail, isSingleTextRecommendation: boolean, hideMedia: boolean, profile?: ProfileInfoFragment, hideBadge: ProfileType[], hideAuthor?: boolean, additionalContexts: SnowplowContext[] };





const textRecommendationTitle = computed(() => {
	return props.textRecommendation.title ?? props.title;
});

const { profile, textRecTitle, textRecommendationHasVideo, shareId } = useTextRecommendation({
	textRecommendation: () => props.textRecommendation,
	title: () => textRecommendationTitle.value ?? null,
});

const route = useRoute();
const fromRecShare = ref(false);
const showOriginalText = ref<boolean>(false);
const displayCardModal = ref<boolean>(false);
const displayTextRecommendationModal = ref(false);

const headline = computed(() => props.textRecommendation.headline);
const originalHeadline = computed(() => props.textRecommendation.originalHeadline);

const displayedHeadline = computed(() => {
	if (showOriginalText.value && originalHeadline.value) return originalHeadline.value;
	return headline.value;
});

const dateWatched = computed(() => {
	return Intl.DateTimeFormat('en-GB', {
		day: '2-digit',
		month: 'long',
		year: 'numeric',
	}).format(new Date(props.textRecommendation.watchedAt));
});

const showWatchedAt = computed(() => {
	return ['JW_EDITOR', 'USER', 'PRO_USER'].includes(props.textRecommendation.profile.profileType);
});

const showUserOpts = computed(() => props.textRecommendation.ownedByUser);

const backgroundImageUrl = computed(
	() =>
		`${JW_CONFIG.IMAGESCALER_URL}${textRecTitle.value?.content.posterUrl
			?.replace('{profile}', 's166')
			.replace('{format}', 'avif')}`
);

async function handleVideoClick() {
	TrackingHelper.trackEvent(
		'youtube_started',
		{
			action: props.title?.objectType.toLowerCase(),
			label: 'titledetail',
			property: 'wtw_recommendation',
			value: 1,
		},
		props.additionalContexts
	);

	await ModalHelper.openModal(
		TrailerModal,
		{
			clips: [props.textRecommendation.video],
			title: props.title,
			source: 'titledetail',
			trackingProperty: 'wtw_recommendation',
			additionalContexts: props.additionalContexts,
		},
		{ cssClass: 'trailer-modal' },
		'',
		true,
		[]
	);
}

function toggleCardModal(display: boolean) {
	displayCardModal.value = display;

	if (displayCardModal.value) {
		TrackingHelper.trackEvent(
			'userinteraction',
			{
				action: 'read_more_clicked',
			},
			props.additionalContexts
		);
	}

	displayCardModal.value ? disableScroll() : enableScroll();
}

function toggleTextRecommendationModal(display: boolean) {
	displayTextRecommendationModal.value = display;

	displayTextRecommendationModal.value ? disableScroll() : enableScroll();
}

const deleteRecommendation = useMutation<DeleteTextRecommendationMutation, DeleteTextRecommendationMutationVariables>(
	DeleteTextRecommendationDocument,
	{
		refetchQueries: ['GetTextRecommendations'],
	}
);
function deleteTextRecommendation() {
	deleteRecommendation.mutate({
		input: {
			id: props.textRecommendation.id,
		},
	});
}
deleteRecommendation.onDone(() => {
	toastSuccess({
		message: 'Recommendation deleted',
		duration: 3000,
	});
});
deleteRecommendation.onError(() => {
	toastError({
		message: 'Failed to delete recommendation',
		duration: 3000,
	});
});

onMounted(() => {
	if (route.hash !== `#${shareId.value}`) return;

	// Waiting a tiny bit because anchor links work right away but the page content
	// is still rendering and shifting (on mounted renders like ads, experiments, etc.)
	// This helps improve the experience.
	setTimeout(() => {
		const elements = [...document.querySelectorAll(route.hash)];
		const element = elements.find(e => e != null && e.checkVisibility());
		if (element != null) {
			element?.scrollIntoView();
			fromRecShare.value = true;
		}
	}, 250);
});

return { __sfc: true,props, textRecommendationTitle, profile, textRecTitle, textRecommendationHasVideo, shareId, route, fromRecShare, showOriginalText, displayCardModal, displayTextRecommendationModal, headline, originalHeadline, displayedHeadline, dateWatched, showWatchedAt, showUserOpts, backgroundImageUrl, handleVideoClick, toggleCardModal, toggleTextRecommendationModal, deleteRecommendation, deleteTextRecommendation, TextRecommendationCardTitleDetailProfile, TextRecommendationModalV2, TextRecommendationCardTitleDetailModal, Popover, FontAwesomeIcon, faCheck, faPlay, faEllipsis, faPencil, faTrash }
}

})